import { useState, useEffect, useCallback } from 'react';
import { FaCheckCircle, FaTimesCircle, FaExclamationTriangle } from 'react-icons/fa';
import axios from 'axios';
import { useStateContext } from '../Views/Context/ContextProvider';

const NetworkStatus = () => {
  const {handleSetPing}=useStateContext();
  const [expiryStatus, setExpiryStatus] = useState({
    isExpired: false,
    expiryDate: null,
    lastChecked: null,
    isLoading: true,
    error: null
  });
//Update context when expiryStatus.isExpired changes
useEffect(() => {
  if (!expiryStatus.isLoading && expiryStatus.isExpired !== null) {
    handleSetPing(expiryStatus.isExpired);
  }
}, [expiryStatus.isExpired, expiryStatus.isLoading, handleSetPing]);

  // Fetch expiry date using Axios
  const fetchExpiryDate = useCallback(async () => {
    try {
      setExpiryStatus(prev => ({ ...prev, isLoading: true, error: null }));
      
      const response = await axios.get('api/check/expiry', {
        headers: {
          'Accept': 'application/json',
        },
        validateStatus: (status) => status < 500 // Don't throw for 4xx errors
      });

      if (!response.data.expiry_date) {
        throw new Error('No expiry date found in response');
      }

//const expiryDate = new Date(response.data.expiry_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
const expiryDate = new Date(response.data.expiry_date);

      setExpiryStatus(prev => ({
        ...prev,
        expiryDate,
        isLoading: false,
        error: null
      }));

      return expiryDate;
    } catch (error) {
      let errorMessage = 'Failed to fetch expiry date';
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      console.error('Error fetching expiry date:', error);
      setExpiryStatus(prev => ({
        ...prev,
        error: errorMessage,
        isLoading: false
      }));
      return null;
    }
  }, []);

  // Check expiry status function with Axios fetch
  const checkExpiryStatus = useCallback(async () => {
    try {
      const expiryDate = await fetchExpiryDate();
      if (!expiryDate) return false; // Return if fetch failed

      const now = new Date();
      const isExpired = now > expiryDate;
      
      setExpiryStatus(prev => ({
        ...prev,
        isExpired,
        lastChecked: now
      }));

      return isExpired;
    } catch (error) {
      console.error('Error checking expiry status:', error);
      return false;
    }
  }, [fetchExpiryDate]);

  // Initial setup
  useEffect(() => {
    checkExpiryStatus();
   
    // Set up regular checks every 24 hours
    const expiryCheckInterval = setInterval(checkExpiryStatus, 24 * 60 * 60 * 1000);
    
    return () => clearInterval(expiryCheckInterval);
  }, [checkExpiryStatus]);


  // ... rest of your component (getStatusColor, getStatusIcon, getStatusText, etc.) remains the same ...
  const getStatusColor = () => {
    if (expiryStatus.isLoading) return '#2196F3';
    if (expiryStatus.error) return '#FF9800';
    return expiryStatus.isExpired ? '#F44336' : '#4CAF50';
  };

  const getStatusIcon = () => {
    if (expiryStatus.isLoading) return <div className="spinner" />;
    if (expiryStatus.error) return <FaExclamationTriangle />;
    return expiryStatus.isExpired ? <FaTimesCircle /> : <FaCheckCircle />;
  };

  const getStatusText = () => {
    if (expiryStatus.isLoading) return 'Checking license...';
    if (expiryStatus.error) return `Error: ${expiryStatus.error}`;
   
    return expiryStatus.isExpired ? 'License Expired' : `License Active Till : ${(expiryStatus.expiryDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
  };

  return (
    <div style={{
      padding: '8px 12px',
      borderRadius: '20px',
      backgroundColor: getStatusColor(),
      color: 'white',
      fontSize: '12px',
      fontWeight: 'bold',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
      transition: 'all 0.3s ease'
    }}>
      <span style={{ fontSize: '14px' }}>
        {getStatusIcon()}
      </span>
      <span>{getStatusText()}</span>
    </div>
  );
};

export default NetworkStatus;